// ============================================================================
// ShareButtons.jsx — Social Share & Pinterest Pin
// Share designs to Pinterest, Facebook, X (Twitter), copy link
// Optional "Made with Zenna" watermark
// ============================================================================

import { useState, useCallback } from 'react'

const API_URL = import.meta.env.VITE_API_URL || ''

// ============================================================================
// WATERMARK UTILITY — Add "Made with Zenna ✨" to image
// ============================================================================

async function addWatermark(imageUrl) {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')

      // Draw original image
      ctx.drawImage(img, 0, 0)

      // Watermark settings
      const fontSize = Math.max(12, Math.floor(img.width * 0.018))
      const padding = fontSize * 0.8
      const text = 'Made with Zenna ✨'

      ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
      const metrics = ctx.measureText(text)
      const textWidth = metrics.width
      const textHeight = fontSize

      // Semi-transparent background pill
      const bgX = img.width - textWidth - padding * 2.5
      const bgY = img.height - textHeight - padding * 2.5
      const bgW = textWidth + padding * 2
      const bgH = textHeight + padding * 1.2
      const radius = bgH / 2

      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)'
      ctx.beginPath()
      ctx.roundRect(bgX, bgY, bgW, bgH, radius)
      ctx.fill()

      // White text
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, bgX + padding, bgY + bgH / 2)

      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => resolve(imageUrl) // fallback to original
    img.src = imageUrl
  })
}

// ============================================================================
// SHARE BUTTONS COMPONENT
// ============================================================================

export default function ShareButtons({
  imageUrl,
  title = '',
  description = '',
  pageUrl = '',
  contentType = 'gallery_post', // gallery_post, listing, project
  contentId = '',
  userEmail = '',
  showWatermarkToggle = true,
  compact = false,
  onShare = null,
  className = ''
}) {
  const [withWatermark, setWithWatermark] = useState(true)
  const [sharing, setSharing] = useState(null) // 'pinterest' | 'facebook' | etc.
  const [copied, setCopied] = useState(false)
  const [showPinterestModal, setShowPinterestModal] = useState(false)
  const [pinterestBoards, setPinterestBoards] = useState([])
  const [selectedBoard, setSelectedBoard] = useState('')
  const [pinDescription, setPinDescription] = useState(description || `${title} — Made with Zenna ✨`)

  const shareUrl = pageUrl || (typeof window !== 'undefined' ? window.location.href : 'https://zennago.com')

  // Track share
  const trackShare = useCallback(async (platform) => {
    if (!contentId || !userEmail) return
    try {
      await fetch(`${API_URL}/community/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
        body: JSON.stringify({ content_type: contentType, content_id: contentId, platform, with_watermark: withWatermark })
      })
    } catch (e) { /* non-critical */ }
  }, [contentType, contentId, userEmail, withWatermark])

  // Get image (with or without watermark)
  const getShareImage = useCallback(async () => {
    if (!imageUrl) return null
    if (withWatermark) return await addWatermark(imageUrl)
    return imageUrl
  }, [imageUrl, withWatermark])

  // ---- PINTEREST ----
  const handlePinterest = useCallback(async () => {
    setSharing('pinterest')
    try {
      // Check if user has Pinterest connected
      const statusRes = await fetch(`${API_URL}/profiles/pinterest/status?email=${userEmail}`, {
        headers: { 'x-user-email': userEmail }
      })
      const statusData = await statusRes.json()

      if (!statusData.connected) {
        // Start OAuth flow
        const connectRes = await fetch(`${API_URL}/profiles/pinterest/connect?email=${userEmail}`, {
          headers: { 'x-user-email': userEmail }
        })
        const connectData = await connectRes.json()
        if (connectData.auth_url) {
          window.open(connectData.auth_url, '_blank', 'width=600,height=700')
        }
        setSharing(null)
        return
      }

      // Get boards for selection
      const boardsRes = await fetch(`${API_URL}/profiles/pinterest/boards?email=${userEmail}`, {
        headers: { 'x-user-email': userEmail }
      })
      const boardsData = await boardsRes.json()
      setPinterestBoards(boardsData.boards || [])
      setShowPinterestModal(true)
    } catch (err) {
      console.error('Pinterest share error:', err)
    } finally {
      setSharing(null)
    }
  }, [userEmail])

  const pinToBoard = useCallback(async () => {
    setSharing('pinterest')
    try {
      const shareImage = await getShareImage()

      const res = await fetch(`${API_URL}/profiles/pinterest/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
        body: JSON.stringify({
          title: title || 'Craft Design',
          description: pinDescription,
          image_url: shareImage || imageUrl,
          link: shareUrl,
          board_id: selectedBoard || undefined
        })
      })

      const data = await res.json()
      if (data.success) {
        trackShare('pinterest')
        if (onShare) onShare('pinterest', data)
        setShowPinterestModal(false)
      }
    } catch (err) {
      console.error('Pin error:', err)
    } finally {
      setSharing(null)
    }
  }, [userEmail, title, pinDescription, imageUrl, shareUrl, selectedBoard, getShareImage, trackShare, onShare])

  // ---- FACEBOOK ----
  const handleFacebook = useCallback(() => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(title || 'Check out my design on Zenna!')}`
    window.open(fbUrl, '_blank', 'width=600,height=400')
    trackShare('facebook')
    if (onShare) onShare('facebook')
  }, [shareUrl, title, trackShare, onShare])

  // ---- X (TWITTER) ----
  const handleTwitter = useCallback(() => {
    const text = `${title || 'Check out my design!'} — Made with @ZennaGo ✨`
    const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`
    window.open(xUrl, '_blank', 'width=600,height=400')
    trackShare('twitter')
    if (onShare) onShare('twitter')
  }, [shareUrl, title, trackShare, onShare])

  // ---- COPY LINK ----
  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      trackShare('copy_link')
      if (onShare) onShare('copy_link')
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      // Fallback
      const textarea = document.createElement('textarea')
      textarea.value = shareUrl
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [shareUrl, trackShare, onShare])

  // ---- DOWNLOAD WITH WATERMARK ----
  const handleDownload = useCallback(async () => {
    setSharing('download')
    try {
      const img = await getShareImage()
      if (!img) return
      const link = document.createElement('a')
      link.href = img
      link.download = `${(title || 'zenna-design').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error('Download error:', err)
    } finally {
      setSharing(null)
    }
  }, [getShareImage, title])

  // ---- NATIVE SHARE (mobile) ----
  const handleNativeShare = useCallback(async () => {
    if (!navigator.share) return handleCopyLink()
    try {
      await navigator.share({
        title: title || 'Zenna Design',
        text: description || 'Check out my craft design!',
        url: shareUrl
      })
      trackShare('native')
    } catch (e) { /* user cancelled */ }
  }, [title, description, shareUrl, handleCopyLink, trackShare])

  const btnBase = compact
    ? 'p-2 rounded-lg transition-all duration-200 hover:scale-105'
    : 'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105'

  return (
    <div className={className}>
      {/* Watermark toggle */}
      {showWatermarkToggle && imageUrl && (
        <label className="flex items-center gap-2 text-xs text-slate-400 mb-2 cursor-pointer">
          <input
            type="checkbox"
            checked={withWatermark}
            onChange={(e) => setWithWatermark(e.target.checked)}
            className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet w-3.5 h-3.5"
          />
          Add "Made with Zenna ✨" watermark
        </label>
      )}

      {/* Share buttons */}
      <div className={compact ? 'flex items-center gap-1.5' : 'flex flex-wrap items-center gap-2'}>
        {/* Pinterest */}
        <button
          onClick={handlePinterest}
          disabled={sharing === 'pinterest'}
          className={`${btnBase} bg-[#E60023]/10 text-[#E60023] hover:bg-[#E60023]/20 border border-[#E60023]/20`}
          title="Pin to Pinterest"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
          </svg>
          {!compact && (sharing === 'pinterest' ? 'Connecting...' : 'Pinterest')}
        </button>

        {/* Facebook */}
        <button
          onClick={handleFacebook}
          className={`${btnBase} bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20 border border-[#1877F2]/20`}
          title="Share on Facebook"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          {!compact && 'Facebook'}
        </button>

        {/* X (Twitter) */}
        <button
          onClick={handleTwitter}
          className={`${btnBase} bg-slate-100/10 text-slate-300 hover:bg-slate-100/20 border border-slate-500/20`}
          title="Share on X"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          {!compact && 'X'}
        </button>

        {/* Copy Link */}
        <button
          onClick={handleCopyLink}
          className={`${btnBase} ${copied ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-slate-100/10 text-slate-400 hover:bg-slate-100/20 border border-slate-500/20'}`}
          title="Copy link"
        >
          {copied ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          )}
          {!compact && (copied ? 'Copied!' : 'Copy Link')}
        </button>

        {/* Download */}
        {imageUrl && (
          <button
            onClick={handleDownload}
            disabled={sharing === 'download'}
            className={`${btnBase} bg-zenna-violet/10 text-zenna-violet hover:bg-zenna-violet/20 border border-zenna-violet/20`}
            title="Download image"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {!compact && (sharing === 'download' ? 'Saving...' : 'Download')}
          </button>
        )}

        {/* Native share (mobile) */}
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={handleNativeShare}
            className={`${btnBase} bg-slate-100/10 text-slate-400 hover:bg-slate-100/20 border border-slate-500/20`}
            title="Share"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            {!compact && 'More'}
          </button>
        )}
      </div>

      {/* Pinterest Board Selection Modal */}
      {showPinterestModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPinterestModal(false)}>
          <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md border border-slate-700" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-[#E60023]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
                </svg>
                Pin to Pinterest
              </h3>
            </div>

            <div className="p-5 space-y-4">
              {/* Preview */}
              {imageUrl && (
                <div className="rounded-xl overflow-hidden border border-slate-700 max-h-48">
                  <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}

              {/* Description */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Description</label>
                <textarea
                  value={pinDescription}
                  onChange={(e) => setPinDescription(e.target.value)}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-zenna-violet"
                  rows={3}
                  maxLength={500}
                />
              </div>

              {/* Board selection */}
              {pinterestBoards.length > 0 && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Board</label>
                  <select
                    value={selectedBoard}
                    onChange={(e) => setSelectedBoard(e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-zenna-violet"
                  >
                    <option value="">Select a board...</option>
                    {pinterestBoards.map(board => (
                      <option key={board.id} value={board.id}>{board.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Watermark toggle */}
              <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={withWatermark}
                  onChange={(e) => setWithWatermark(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-700 text-zenna-violet focus:ring-zenna-violet"
                />
                Add "Made with Zenna ✨" watermark
              </label>
            </div>

            <div className="p-5 border-t border-slate-700 flex gap-3">
              <button
                onClick={() => setShowPinterestModal(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-slate-400 bg-slate-700/50 hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={pinToBoard}
                disabled={sharing === 'pinterest'}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-[#E60023] hover:bg-[#CC0020] disabled:opacity-50"
              >
                {sharing === 'pinterest' ? 'Pinning...' : '📌 Pin It!'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}